import type { PlayerCommand } from '../sim/commands';
import type { FactionId, MapId, MatchMode, TeamId } from '../content/types';
import { contentHash, PROTOCOL_VERSION } from './content';
import type { ClientMessage, NetworkPlayerCommand, ServerMessage } from './protocol';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
type Listener = (message: ServerMessage) => void;

export class OnlineClient {
  private socket: WebSocket | null = null;
  private closed = false;
  private reconnectTimer: number | null = null;
  private requestCounter = 0;
  private commandSequence = 0;
  private reconnectAttempts = 0;
  private readonly commandSentAt = new Map<number, number>();
  private readonly listeners = new Set<Listener>();
  private readonly statusListeners = new Set<(status: ConnectionStatus) => void>();
  private statusValue: ConnectionStatus = 'disconnected';
  token = '';
  sessionId = '';

  constructor(readonly url: string) {
    try { this.token = sessionStorage.getItem(this.storageKey()) ?? ''; } catch { this.token = ''; }
  }

  get status(): ConnectionStatus { return this.statusValue; }

  onMessage(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  connect(): void {
    this.closed = false;
    this.setStatus(this.token ? 'reconnecting' : 'connecting');
    const socket = new WebSocket(this.url);
    this.socket = socket;
    socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.setStatus('connected');
      this.rawSend({ type: 'hello', protocolVersion: PROTOCOL_VERSION, contentHash: contentHash(), token: this.token || undefined });
    };
    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(String(event.data)) as ServerMessage;
        if (message.type === 'session') {
          this.token = message.token;
          this.sessionId = message.sessionId;
          try { sessionStorage.setItem(this.storageKey(), this.token); } catch { /* storage may be unavailable */ }
        } else if (message.type === 'resumeMatch') {
          this.commandSequence = Math.max(this.commandSequence, message.lastClientSeq);
        } else if (message.type === 'commandAck') {
          const sentAt = this.commandSentAt.get(message.clientSeq);
          if (sentAt !== undefined) {
            console.info(JSON.stringify({ event: 'commandRtt', clientSeq: message.clientSeq, status: message.status, rttMs: performance.now() - sentAt }));
            this.commandSentAt.delete(message.clientSeq);
          }
        }
        for (const listener of this.listeners) listener(message);
      } catch {
        // Ignore malformed server output; the server will close incompatible clients.
      }
    };
    socket.onerror = () => undefined;
    socket.onclose = () => {
      if (this.socket === socket) this.socket = null;
      if (!this.closed) {
        this.setStatus('reconnecting');
        const base = Math.min(5000, 1000 * 2 ** Math.min(3, this.reconnectAttempts++));
        const delay = base * (0.8 + Math.random() * 0.4);
        this.reconnectTimer = globalThis.setTimeout(() => this.connect(), delay) as unknown as number;
      } else {
        this.setStatus('disconnected');
      }
    };
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer !== null) globalThis.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.socket?.close();
    this.socket = null;
    this.token = '';
    this.sessionId = '';
    try { sessionStorage.removeItem(this.storageKey()); } catch { /* storage may be unavailable */ }
    this.setStatus('disconnected');
  }

  createRoom(mode: MatchMode): void {
    this.rawSend({ type: 'createRoom', requestId: this.requestId(), mode });
  }

  joinRoom(code: string): void {
    this.rawSend({ type: 'joinRoom', requestId: this.requestId(), code: code.trim().toUpperCase() });
  }

  queue(mode: MatchMode, faction: FactionId, deck: string[]): void {
    this.rawSend({ type: 'queue', requestId: this.requestId(), mode, faction, deck });
  }

  setRoomTeam(team: TeamId): void {
    this.rawSend({ type: 'setRoomTeam', requestId: this.requestId(), team });
  }

  setRoomMapVote(mapId: MapId): void {
    this.rawSend({ type: 'setRoomMapVote', requestId: this.requestId(), mapId });
  }

  ready(ready: boolean, faction: FactionId, deck: string[]): void {
    this.rawSend({ type: 'ready', requestId: this.requestId(), ready, faction, deck });
  }

  sendCommand(command: PlayerCommand): void {
    const { player: _player, ...payload } = command;
    const clientSeq = ++this.commandSequence;
    this.commandSentAt.set(clientSeq, performance.now());
    if (!this.rawSend({ type: 'command', clientSeq, command: payload as NetworkPlayerCommand })) this.commandSentAt.delete(clientSeq);
  }

  acknowledgeSnapshot(matchId: string, snapshotSeq: number): void {
    this.rawSend({ type: 'snapshotAck', matchId, snapshotSeq });
  }

  requestResync(matchId: string): void {
    this.rawSend({ type: 'resync', matchId });
  }

  leave(): void {
    this.rawSend({ type: 'leave' });
  }

  concede(): void {
    this.rawSend({ type: 'concede' });
  }

  private requestId(): string { return `${this.sessionId || 'guest'}-${++this.requestCounter}`; }

  private rawSend(message: ClientMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  private storageKey(): string { return `sigilfront.session.${this.url}`; }

  private setStatus(status: ConnectionStatus): void {
    this.statusValue = status;
    for (const listener of this.statusListeners) listener(status);
  }
}
